'use client'

import { Fragment, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import {
  FieldGroup,
  FieldLabel,
  LabelWithTooltip,
  PrimaryButton,
  SelectField,
  TextareaField,
  TextField,
} from '@/app/FormFields'
import type { KnowledgeEntryKind } from '@/db/schema'
import { isOnlyKnowledgeMarkdownSample, KNOWLEDGE_MARKDOWN_SAMPLE } from '@/lib/knowledge-markdown-sample'

type KnowledgeFormProps = {
  entry?: {
    id: string
    title: string
    slug: string
    kind: KnowledgeEntryKind
    tags: string[]
    bodyMarkdown: string
  }
}

const KINDS: KnowledgeEntryKind[] = ['index', 'log', 'entity', 'concept', 'procedure']

function renderInlineMarkdown(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean)

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={index} className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-[0.85em] dark:bg-zinc-800">
          {part.slice(1, -1)}
        </code>
      )
    }
    return <Fragment key={index}>{part}</Fragment>
  })
}

function renderMarkdownPreview(markdown: string) {
  const lines = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let paragraph: string[] = []
  let listItems: string[] = []
  let listType: 'bullet' | 'numbered' | null = null

  function flushParagraph() {
    if (paragraph.length === 0) return
    const text = paragraph.join(' ').trim()
    if (text) {
      blocks.push(
        <p key={`p-${blocks.length}`} className="text-sm leading-6 text-zinc-700 dark:text-zinc-300">
          {renderInlineMarkdown(text)}
        </p>,
      )
    }
    paragraph = []
  }

  function flushList() {
    if (!listType || listItems.length === 0) return
    const ListTag = listType === 'numbered' ? 'ol' : 'ul'
    blocks.push(
      <ListTag
        key={`list-${blocks.length}`}
        className={`${listType === 'numbered' ? 'list-decimal' : 'list-disc'} space-y-1 pl-5 text-sm leading-6 text-zinc-700 dark:text-zinc-300`}
      >
        {listItems.map((item, index) => (
          <li key={index}>{renderInlineMarkdown(item)}</li>
        ))}
      </ListTag>,
    )
    listItems = []
    listType = null
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()
    if (!line) {
      flushParagraph()
      flushList()
      continue
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/)
    if (heading) {
      flushParagraph()
      flushList()
      const level = heading[1].length
      const className =
        level === 1
          ? 'text-lg font-semibold text-zinc-950 dark:text-white'
          : level === 2
            ? 'text-base font-semibold text-zinc-950 dark:text-white'
            : 'text-sm font-semibold text-zinc-950 dark:text-white'
      const HeadingTag = (`h${Math.min(level + 1, 4)}`) as 'h2' | 'h3' | 'h4'
      blocks.push(
        <HeadingTag key={`h-${blocks.length}`} className={className}>
          {renderInlineMarkdown(heading[2])}
        </HeadingTag>,
      )
      continue
    }

    const bullet = line.match(/^[-*]\s+(.+)$/)
    if (bullet) {
      flushParagraph()
      if (listType !== 'bullet') flushList()
      listType = 'bullet'
      listItems.push(bullet[1])
      continue
    }

    const numbered = line.match(/^\d+\.\s+(.+)$/)
    if (numbered) {
      flushParagraph()
      if (listType !== 'numbered') flushList()
      listType = 'numbered'
      listItems.push(numbered[1])
      continue
    }

    flushList()
    paragraph.push(line)
  }

  flushParagraph()
  flushList()

  return blocks
}

export default function KnowledgeForm({ entry }: KnowledgeFormProps) {
  const router = useRouter()
  const [title, setTitle] = useState(entry?.title ?? '')
  const [slug, setSlug] = useState(entry?.slug ?? '')
  const [kind, setKind] = useState<KnowledgeEntryKind>(entry?.kind ?? 'concept')
  const [tags, setTags] = useState<string[]>(entry?.tags ?? [])
  const [tagInput, setTagInput] = useState('')
  const [bodyMarkdown, setBodyMarkdown] = useState(entry?.bodyMarkdown ?? KNOWLEDGE_MARKDOWN_SAMPLE)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)

  const preview = useMemo(() => {
    const trimmed = bodyMarkdown.trim()
    return trimmed ? renderMarkdownPreview(trimmed) : null
  }, [bodyMarkdown])

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!entry && isOnlyKnowledgeMarkdownSample(bodyMarkdown)) {
      setError('Replace the sample markdown with verified knowledge before creating an entry.')
      return
    }

    setSaving(true)

    const response = await fetch(entry ? `/api/knowledge/${entry.id}` : '/api/knowledge', {
      method: entry ? 'PUT' : 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ title, slug, kind, tags, bodyMarkdown }),
    })

    const payload = await response.json().catch(() => null) as
      | { entry?: { id: string }; error?: string }
      | null

    setSaving(false)
    if (!response.ok || !payload?.entry?.id) {
      setError(payload?.error ?? 'Unable to save knowledge entry')
      return
    }

    router.push(`/knowledge?entry=${payload.entry.id}`)
    router.refresh()
  }

  function addTag() {
    const value = tagInput.toLowerCase().trim().replace(/\s+/g, '-')
    if (!value || tags.includes(value) || tags.length >= 10) {
      setTagInput('')
      return
    }
    setTags((current) => [...current, value])
    setTagInput('')
  }

  function removeTag(tag: string) {
    setTags((current) => current.filter((item) => item !== tag))
  }

  function handleTagKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      addTag()
    } else if (event.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-5 lg:grid-cols-[minmax(0,11fr)_minmax(18rem,5fr)]">
      <div className="space-y-4 rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <FieldGroup>
          <FieldLabel htmlFor="kb-title">Title</FieldLabel>
          <TextField
            id="kb-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Line 3 filler E42 fault"
            required
          />
        </FieldGroup>

        <div className="grid gap-4 md:grid-cols-[1fr_180px]">
          <FieldGroup>
            <LabelWithTooltip
              htmlFor="kb-slug"
              tooltipId="kb-slug-help"
              tooltipText="The slug becomes the stable URL-friendly identifier for this entry. Leave it blank to generate one from the title."
            >
              Slug
            </LabelWithTooltip>
            <TextField
              id="kb-slug"
              value={slug}
              onChange={(event) => setSlug(event.target.value)}
              placeholder="auto-generated if blank"
              aria-describedby="kb-slug-help"
            />
          </FieldGroup>
          <FieldGroup>
            <LabelWithTooltip
              htmlFor="kb-kind"
              tooltipId="kb-kind-help"
              tooltipText="Choose the knowledge shape: concept for background, procedure for steps, entity for equipment or systems, log for observed events, and index for navigation."
            >
              Type
            </LabelWithTooltip>
            <SelectField
              id="kb-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as KnowledgeEntryKind)}
              aria-describedby="kb-kind-help"
            >
              {KINDS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </SelectField>
          </FieldGroup>
        </div>

        <FieldGroup>
          <LabelWithTooltip
            htmlFor="kb-tags"
            tooltipId="kb-tags-help"
            tooltipText="Tags help experts find related guidance and improve retrieval context for future AI drafts."
          >
            Tags
          </LabelWithTooltip>
          <div
            className="flex min-h-11 cursor-text flex-wrap items-center gap-2 rounded-xl border border-zinc-300 bg-white px-3 py-2 shadow-sm transition focus-within:border-[var(--brand-green)] focus-within:ring-4 focus-within:ring-[color-mix(in_srgb,var(--brand-green)_16%,transparent)] dark:border-zinc-700 dark:bg-zinc-950"
            onClick={() => tagInputRef.current?.focus()}
          >
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
              >
                {tag}
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    removeTag(tag)
                  }}
                  className="leading-none text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-100"
                  aria-label={`Remove tag ${tag}`}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              ref={tagInputRef}
              id="kb-tags"
              value={tagInput}
              onChange={(event) => setTagInput(event.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={addTag}
              placeholder={tags.length === 0 ? 'Add tags...' : ''}
              disabled={tags.length >= 10}
              className="min-w-[120px] flex-1 bg-transparent text-sm text-zinc-950 outline-none placeholder:text-zinc-500 dark:text-zinc-100 dark:placeholder:text-zinc-500"
              aria-describedby="kb-tags-help"
            />
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">Press Enter or comma to add. Up to 10 tags.</p>
        </FieldGroup>

        <FieldGroup>
          <LabelWithTooltip
            htmlFor="kb-markdown"
            tooltipId="kb-markdown-help"
            tooltipText="Replace the sample with verified, reusable guidance. This content can be retrieved for future AI-assisted draft responses."
          >
            Markdown
          </LabelWithTooltip>
          <TextareaField
            id="kb-markdown"
            value={bodyMarkdown}
            onChange={(event) => setBodyMarkdown(event.target.value)}
            className="min-h-[420px] font-mono text-sm"
            required
            aria-describedby="kb-markdown-help"
          />
        </FieldGroup>

        {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}

        <PrimaryButton
          type="submit"
          disabled={saving}
        >
          {saving ? 'Saving...' : entry ? 'Update entry' : 'Create entry'}
        </PrimaryButton>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-zinc-950 dark:text-white">Preview</h2>
        <aside className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
          <div className="space-y-3">
            {preview ?? (
              <p className="text-sm leading-6 text-zinc-500 dark:text-zinc-400">
                Markdown preview appears here while you write.
              </p>
            )}
          </div>
        </aside>
      </div>
    </form>
  )
}
